import React, { useEffect, useMemo, useState } from 'react';
import styles from './BulkBindTemplates.module.css';
import Button from '../Button/Button';
import Spinner from '../Spinner/Spinner';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import BindMatrix from '../BindMatrix/BindMatrix';
import useApi from '../../hooks/useApi.hook';
import useFillSelect from '../../hooks/useFillSelect.hook';
import useProfessionRows from '../../hooks/useProfessionRows.hook';
import {
	getAllTemplateBindsUrl,
	getAllFileTemplatesUrl,
	createTemplateBindsManyUrl,
	deleteTemplateBindsManyUrl
} from '../../helpers/constants';
import useTemplateRegistry from '../../hooks/useTemplateRegistry.hook';


export default function BulkBindTemplates({ professions }) {
	const api = useApi();
	const [loading, setLoading] = useState(false);
	const [saveOk, setSaveOk] = useState(false);
	const [resetOk, setResetOk] = useState(false);
	const [errorApi, setErrorApi] = useState(undefined);
	const [currentState, setCurrentState] = useState(() => new Set());
	const [isDirty, setIsDirty] = useState(false);

	const {
		itemDict: bindsDict,
		getItems: getBinds,
		loading: bindsLoading
	} = useFillSelect({ endpoint: getAllTemplateBindsUrl, labelField: 'id' });

	const {
		itemDict: fileTemplatesDict,
		loading: templatesLoading
	} = useFillSelect({ endpoint: getAllFileTemplatesUrl, labelField: 'file_name' });

	const binds = useMemo(() => Object.values(bindsDict), [bindsDict]);

	const baseState = useMemo(() => {
		const s = new Set();
		binds.forEach(b => s.add(`${b.profession_id}-${b.file_template_id}`));
		return s;
	}, [binds]);

	useEffect(() => {
		setCurrentState(new Set(baseState));
		setIsDirty(false);
	}, [baseState]);

	const {groupTemplates} = useTemplateRegistry();
	const registryPersonal = groupTemplates('personal');

	const personalTemplates = useMemo(() => {
		const byFileName = Object.fromEntries(
			Object.values(fileTemplatesDict).map(ft => [ft.file_name, ft])
		);
		return registryPersonal
			.filter(t => byFileName[t.code])
			.map(t => ({ id: byFileName[t.code].id, label: t.title }))
			.sort((a, b) => a.label.localeCompare(b.label));
	}, [fileTemplatesDict, registryPersonal]);

	const rowItems = useProfessionRows(professions);

	const toggleAll = (colId) => {
		const allChecked = rowItems.every(row => currentState.has(`${row.id}-${colId}`));
		setCurrentState(prev => {
			const next = new Set(prev);
			rowItems.forEach(row => {
				const key = `${row.id}-${colId}`;
				if (allChecked) next.delete(key);
				else next.add(key);
			});
			return next;
		});
		setIsDirty(true);
	};

	const toggleCell = (rowId, colId) => {
		const key = `${rowId}-${colId}`;
		setCurrentState(prev => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
		setIsDirty(true);
	};

	const resetChanges = () => {
		setCurrentState(new Set(baseState));
		setIsDirty(false);
		setResetOk(true);
		setTimeout(() => setResetOk(false), 1500);
	};

	const groupByTemplate = (pairs) => {
		const map = {};
		pairs.forEach(key => {
			const [profId, templateId] = key.split('-').map(Number);
			if (!map[templateId]) map[templateId] = [];
			map[templateId].push(profId);
		});
		return map;
	};

	const saveChanges = async () => {
		const toAdd = [...currentState].filter(k => !baseState.has(k));
		const toRemove = [...baseState].filter(k => !currentState.has(k));

		const addGroups = groupByTemplate(toAdd);
		const removeGroups = groupByTemplate(toRemove);

		setLoading(true);
		setErrorApi(undefined);
		try {
			const addRequests = Object.entries(addGroups).map(([templateId, profIds]) =>
				api.post(createTemplateBindsManyUrl, {
					file_template_ids: [Number(templateId)],
					profession_ids: profIds,
					bind_to_all: false
				})
			);
			const removeRequests = Object.entries(removeGroups).map(([templateId, profIds]) =>
				api.delete(deleteTemplateBindsManyUrl, {
					data: {
						file_template_ids: [Number(templateId)],
						profession_ids: profIds
					}
				})
			);
			await Promise.all([...addRequests, ...removeRequests]);
			await getBinds();
			setSaveOk(true);
			setTimeout(() => setSaveOk(false), 1500);
		} catch (e) {
			setErrorApi(e.response?.data?.detail || e.response?.data?.message || 'Неизвестная ошибка');
			await getBinds();
		} finally {
			setLoading(false);
		}
	};

	if (bindsLoading || templatesLoading) {
		return <div className={styles.loadingWrap}><Spinner /></div>;
	}

	return (
		<div className={styles.container}>
			<ErrorMessage error={errorApi} onClose={() => setErrorApi(undefined)} />
			<BindMatrix
				loading={loading}
				rows={rowItems}
				columns={personalTemplates}
				currentState={currentState}
				baseState={baseState}
				toggleCell={toggleCell}
				toggleAll={toggleAll}
			/>
			<div className={styles.actions}>
				<Button
					onClick={saveChanges}
					disabled={!isDirty || loading}
					className={`${styles.btn} ${saveOk ? styles.btnOk : ''}`}
				>
					{saveOk ? '✓ Сохранено' : 'Сохранить'}
				</Button>
				<Button
					onClick={resetChanges}
					disabled={!isDirty || loading}
					className={`${styles.btn} ${resetOk ? styles.btnOk : ''}`}
				>
					{resetOk ? '✓ Сброшено' : 'Сбросить'}
				</Button>
			</div>
		</div>
	);
}